import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OTPInput } from '@/components/ui/otp-input';
import { Loader2, Users, Phone, MessageSquare, ArrowLeft, CheckCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { sendOTP, verifyOTP, resendOTP } from '@/lib/services/otp-verification';
import { sendWhatsAppOTP, verifyWhatsAppOTP } from '@/lib/firebase/auth/whatsapp-phone-auth';
import { addMemberToOrganization, isOrganizationMember } from '@/lib/firebase/queries/organizations';
import { signInWithGoogle } from '@/lib/firebase/auth/google';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useOTPMethod } from '@/hooks/use-otp-method';
import type { CustomerFormData } from './types';
import type { Group } from '@/types/support';

interface PublicCustomerDialogFormWithOTPProps {
  onSubmit: (data: CustomerFormData & { selectedSectionId?: string; selectedGroupId?: string }) => Promise<void>;
  isLoading: boolean;
  title: string;
  groups?: Group[];
  showGroupSelection?: boolean;
  defaultSectionId?: string;
  defaultGroupId?: string;
  embedded?: boolean; // New prop to control if this is embedded in another form
  organizationId?: string; // For adding existing users to organization
  onExistingUserLogin?: (userId: string) => Promise<void>; // Callback for existing user login
}

type FormStep = 'phone' | 'otp' | 'details' | 'groups';

export function PublicCustomerDialogFormWithOTP({
  onSubmit,
  isLoading,
  title,
  groups = [],
  showGroupSelection = false,
  defaultSectionId,
  defaultGroupId,
  embedded = false,
  organizationId,
  onExistingUserLogin
}: PublicCustomerDialogFormWithOTPProps) {
  const { t, dir } = useLanguage();
  const { otpMethod } = useOTPMethod();
  const { loginWithWhatsAppOTP } = useAuth();
  const [currentStep, setCurrentStep] = useState<FormStep>('phone');
  const [verifiedPhone, setVerifiedPhone] = useState<string>('');
  const [otpValue, setOtpValue] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpExpiry, setOtpExpiry] = useState<Date | null>(null);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [whatsappVerificationId, setWhatsappVerificationId] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [existingUserData, setExistingUserData] = useState<any>(null);
  
  // Ref for submit button to auto-focus when OTP is complete
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  // Phone verification form
  const phoneSchema = z.object({
    phone: z.string()
      .regex(/^05\d{8}$/, t('validation.phoneInvalidFormat') || 'Phone number must be in format 05XXXXXXXX (10 digits starting with 05)')
      .length(10, t('validation.phoneExactLength') || 'Phone number must be exactly 10 digits'),
  });

  const phoneForm = useForm<{ phone: string }>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { phone: '' }
  });

  // Get sections (parent groups) and groups (subgroups) - move before schema
  const sections = groups.filter(group => group.groupType === 'parent');
  const subgroups = groups.filter(group => group.groupType !== 'parent');
  
  // Only show group selection if there are actual subgroups that can accept members
  const hasJoinableGroups = subgroups.length > 0;

  // Group selection form for existing users
  const groupSelectionSchema = z.object({
    selectedSectionId: showGroupSelection && hasJoinableGroups && sections.length > 0
      ? z.string().min(1, t('validation.sectionRequired') || 'Please select a section')
      : z.string().optional(),
    selectedGroupId: showGroupSelection && hasJoinableGroups && sections.length > 0
      ? z.string().min(1, t('validation.groupRequired') || 'Please select a group')
      : z.string().optional()
  });

  const groupForm = useForm<{ selectedSectionId?: string; selectedGroupId?: string }>({
    resolver: zodResolver(groupSelectionSchema),
    defaultValues: {
      selectedSectionId: defaultSectionId || '',
      selectedGroupId: defaultGroupId || ''
    }
  });

  // Main registration form
  const formSchema = z.object({
    firstNameEn: z.string().min(2, t('validation.firstNameEnMinLength') || 'First name (English) must be at least 2 characters'),
    lastNameEn: z.string().min(2, t('validation.lastNameEnMinLength') || 'Last name (English) must be at least 2 characters'),
    firstNameAr: z.string().min(2, t('validation.firstNameArMinLength') || 'First name (Arabic) must be at least 2 characters'),
    lastNameAr: z.string().min(2, t('validation.lastNameArMinLength') || 'Last name (Arabic) must be at least 2 characters'),
    selectedSectionId: showGroupSelection 
      ? z.string().min(1, t('validation.sectionRequired') || 'Please select a section')
      : z.string().optional(),
    selectedGroupId: showGroupSelection 
      ? z.string().min(1, t('validation.groupRequired') || 'Please select a group')
      : z.string().optional()
  });
  
  const form = useForm<Omit<CustomerFormData, 'phone'> & { selectedSectionId?: string; selectedGroupId?: string }>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstNameEn: '',
      lastNameEn: '',
      firstNameAr: '',
      lastNameAr: '',
      selectedSectionId: defaultSectionId || '',
      selectedGroupId: defaultGroupId || ''
    }
  });

  // Watch for selected section IDs
  const selectedSectionId = form.watch('selectedSectionId');
  const selectedGroupSectionId = groupForm.watch('selectedSectionId');
  
  // Filter available groups based on selected sections
  const availableGroups = groups.filter(group => 
    group.groupType !== 'parent' && group.parentGroupId === selectedSectionId
  );
  const availableGroupsForExisting = groups.filter(group => 
    group.groupType !== 'parent' && group.parentGroupId === selectedGroupSectionId
  );

  // Cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // OTP expiry timer effect
  useEffect(() => {
    if (otpExpiry) {
      const timer = setInterval(() => {
        const now = new Date();
        if (now >= otpExpiry) {
          setOtpExpiry(null);
          toast.error(t('otp.errors.otpExpired') || 'Verification code has expired');
          setCurrentStep('phone');
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [otpExpiry, t]);

  const handleSendOTP = async (data: { phone: string }) => {
    try {
      setIsOtpLoading(true);
      const language = dir === 'rtl' ? 'ar' : 'en';
      
      // First try to send OTP for new user registration
      const result = await sendOTP(data.phone, language);
      
      if (result.success) {
        // New user registration flow
        setIsExistingUser(false);
        setVerifiedPhone(data.phone);
        setCurrentStep('otp');
        setOtpExpiry(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes from now
        setResendCooldown(60); // 1 minute cooldown
        toast.success(t('otp.success.otpSent') || (otpMethod === 'sms' ? 'Verification code sent via SMS' : 'Verification code sent to your WhatsApp'));
      } else if (result.error?.includes('already registered')) {
        // Phone is already registered - use login OTP instead
        toast.info(t('publicRegistration.existingUserDetected') || 'Phone number is already registered. Sending login code...');
        
        // Format phone number for WhatsApp OTP login
        const formattedPhone = `+966${data.phone.substring(1)}`;

        const whatsappResult = await sendWhatsAppOTP(formattedPhone, language as 'en' | 'ar');
        
        if (whatsappResult.success && whatsappResult.verificationId) {
          setIsExistingUser(true);
          setWhatsappVerificationId(whatsappResult.verificationId);
          setVerifiedPhone(data.phone);
          setCurrentStep('otp');
          setOtpExpiry(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes from now
          setResendCooldown(60); // 1 minute cooldown
          toast.success(t('otp.success.otpSent') || (otpMethod === 'sms' ? 'Login verification code sent via SMS' : 'Login verification code sent to your WhatsApp'));
      } else {
          toast.error(whatsappResult.error || 'Failed to send login verification code');
        }
      } else {
        // Other errors
        let errorMessage = result.error || t('otp.errors.sendFailed') || 'Failed to send verification code';
        
        if (result.error?.includes('already reserved')) {
          errorMessage = t('otp.errors.phoneAlreadyReserved') || errorMessage;
        } else if (result.error?.includes('Failed to check')) {
          errorMessage = t('otp.errors.phoneAvailabilityCheckFailed') || errorMessage;
        }
        
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error(t('otp.errors.sendFailed') || 'Failed to send verification code');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpValue.length !== 4) {
      toast.error(t('otp.errors.otpInvalid') || 'Please enter a valid 4-digit code');
      return;
    }

    try {
      setIsOtpLoading(true);
      
             if (isExistingUser && whatsappVerificationId) {
         // Existing user login flow
         const result = await verifyWhatsAppOTP(whatsappVerificationId, otpValue);
         
         if (result.success && result.userData) {
           setExistingUserData(result.userData);
           
           // Check if group selection is required
           if (showGroupSelection && hasJoinableGroups && sections.length > 0) {
             // Show group selection step for existing user
             setCurrentStep('groups');
             toast.success(t('otp.success.phoneVerified') || 'Phone verified! Please select your section and group.');
           } else {
             // No group selection needed, proceed with adding to organization
             await handleExistingUserComplete(result.userData);
           }
         } else {
           toast.error(result.error || t('otp.errors.otpInvalid') || 'Invalid verification code');
           setOtpValue(''); // Clear invalid OTP
         }
      } else {
        // New user registration flow
      const result = await verifyOTP(verifiedPhone, otpValue);
      
      if (result.success) {
        setCurrentStep('details');
        toast.success(t('otp.success.phoneVerified') || 'Phone number verified successfully');
      } else {
        toast.error(result.error || t('otp.errors.otpInvalid') || 'Invalid verification code');
        setOtpValue(''); // Clear invalid OTP
        }
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(t('otp.errors.otpInvalid') || 'Invalid verification code');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsResendLoading(true);
      const language = dir === 'rtl' ? 'ar' : 'en';
      const result = await resendOTP(verifiedPhone, language);
      
      if (result.success) {
        setOtpExpiry(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes from now
        setResendCooldown(60); // 1 minute cooldown
        setOtpValue(''); // Clear current OTP
        toast.success(t('otp.success.otpSent') || (otpMethod === 'sms' ? 'New verification code sent via SMS' : 'New verification code sent to your WhatsApp'));
      } else {
        // Show specific error messages based on the error type
        let errorMessage = result.error || t('otp.errors.sendFailed') || 'Failed to send verification code';
        
        if (result.error?.includes('already registered')) {
          errorMessage = t('otp.errors.phoneAlreadyRegistered') || errorMessage;
        } else if (result.error?.includes('already reserved')) {
          errorMessage = t('otp.errors.phoneAlreadyReserved') || errorMessage;
        } else if (result.error?.includes('Failed to check')) {
          errorMessage = t('otp.errors.phoneAvailabilityCheckFailed') || errorMessage;
        }
        
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error(t('otp.errors.sendFailed') || 'Failed to send verification code');
    } finally {
      setIsResendLoading(false);
    }
  };

  const handleOTPComplete = () => {
    // Auto-focus on submit button when OTP is complete
    setTimeout(() => {
      submitButtonRef.current?.focus();
    }, 100);
  };

  const handleExistingUserComplete = async (userData: any, groupData?: { selectedSectionId?: string; selectedGroupId?: string }) => {
    try {
      // Add user to organization if provided
      if (organizationId && onExistingUserLogin) {
        await onExistingUserLogin(userData.id);
      }
      
      // Use the loginWithWhatsAppOTP function to properly authenticate and navigate
      await loginWithWhatsAppOTP(verifiedPhone);
      
      toast.success(t('publicRegistration.addedToOrganization') || 'Welcome! You have been added to the organization.');
     
     // Reset form state
     phoneForm.reset();
     groupForm.reset();
     setCurrentStep('phone');
     setVerifiedPhone('');
     setOtpValue('');
     setOtpExpiry(null);
     setResendCooldown(0);
     setIsExistingUser(false);
     setWhatsappVerificationId(null);
     setExistingUserData(null);
     
     // Navigation will be handled by loginWithWhatsAppOTP function
    } catch (error) {
      console.error('Error completing existing user login:', error);
      toast.error('Failed to complete login. Please try again.');
    }
  };

  const handleBackToPhone = () => {
    setCurrentStep('phone');
    setVerifiedPhone('');
    setOtpValue('');
    setOtpExpiry(null);
    setResendCooldown(0);
    setIsExistingUser(false);
    setExistingUserData(null);
  };

  const handleGroupSelection = async (groupData: { selectedSectionId?: string; selectedGroupId?: string }) => {
    if (!existingUserData) {
      toast.error('User data not found. Please try again.');
      return;
    }

    await handleExistingUserComplete(existingUserData, groupData);
  };

  const handleGoogleSignIn = async () => {
    if (!organizationId) {
      toast.error('Organization ID is required');
      return;
    }

    try {
      setIsGoogleLoading(true);
      
      const { user, userData } = await signInWithGoogle();
      
             if (userData) {
         // Check if user is already a member of this organization
         const alreadyMember = await isOrganizationMember(organizationId, userData.id);
         
         if (alreadyMember) {
           // User is already a member, just redirect to dashboard
           toast.success(t('publicRegistration.alreadyMemberOfOrg') || 'Welcome back! You are already a member of this organization.');
           window.location.href = '/dashboard/customer';
         } else {
           // Check if group selection is required for new organization member
           if (showGroupSelection && hasJoinableGroups && sections.length > 0) {
             // Show group selection step for Google user
             setExistingUserData(userData);
             setIsExistingUser(true);
             setCurrentStep('groups');
             toast.success(t('auth.googleSignInSuccess') || 'Google sign-in successful! Please select your section and group.');
           } else {
             // No group selection needed, proceed with adding to organization
             if (onExistingUserLogin) {
               await onExistingUserLogin(userData.id);
               toast.success(t('publicRegistration.addedToOrganization') || 'Welcome! You have been added to the organization.');
             }
             
             window.location.href = '/dashboard/customer';
           }
         }
       }
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      
      // Handle specific error messages
      if (error.message?.includes('cancelled') || error.message?.includes('closed')) {
        // User cancelled, don't show error
        return;
      }
      
      if (error.message?.startsWith('auth.')) {
        toast.error(t(error.message) || 'Google sign-in failed');
      } else {
        toast.error(error.message || 'Google sign-in failed');
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleFinalSubmit = async (values: Omit<CustomerFormData, 'phone'> & { selectedSectionId?: string; selectedGroupId?: string }) => {
    const finalData = {
      ...values,
      phone: verifiedPhone // Add the verified phone number
    };
    await onSubmit(finalData);
    
    // Reset all forms and state
    phoneForm.reset();
    form.reset();
    setCurrentStep('phone');
    setVerifiedPhone('');
    setOtpValue('');
    setOtpExpiry(null);
    setResendCooldown(0);
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'phone':
        return t('otp.phoneLabel') || 'Phone Number';
      case 'otp':
        return t('otp.title') || 'Phone Verification';
      case 'groups':
        return t('publicRegistration.selectGroupTitle') || 'Select Your Section and Group';
      case 'details':
        return title;
      default:
        return title;
    }
  };

  const renderTimeRemaining = () => {
    if (!otpExpiry) return null;
    
    const now = new Date();
    const remaining = Math.max(0, Math.floor((otpExpiry.getTime() - now.getTime()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    
    return (
      <p className="text-sm text-muted-foreground text-center">
        {t('otp.info.codeExpiry')?.replace('{{minutes}}', `${minutes}:${seconds.toString().padStart(2, '0')}`) || 
          `Code expires in ${minutes}:${seconds.toString().padStart(2, '0')}`}
      </p>
    );
  };

  const content = (
    <>
      {/* Step 1: Phone Number */}
      {currentStep === 'phone' && (
        <div className="space-y-4">
          {!embedded && (
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Phone className="h-5 w-5" />
                <h3 className="text-lg font-semibold">{t('otp.phoneLabel') || 'Phone Number'}</h3>
              </div>
            </div>
          )}
          <Form {...phoneForm}>
            <form onSubmit={phoneForm.handleSubmit(handleSendOTP)} className="space-y-4">
              <FormField
                  control={phoneForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('otp.phoneLabel') || 'Phone Number'}</FormLabel>
                      <FormControl>
                        <Input 
                          type="tel"
                          placeholder="05XXXXXXXX"
                          disabled={isOtpLoading} 
                          className="h-11 text-base"
                          maxLength={10}
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              <Button 
                type="submit" 
                disabled={isOtpLoading} 
                className="w-full h-11"
              >
                {isOtpLoading ? (
                  <>
                    <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    {t('otp.sendOtp') || 'Sending...'}
                  </>
                ) : (
                  t('otp.sendOtp') || 'Send Verification Code'
                )}
              </Button>
            </form>
          </Form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('auth.orContinueWith') || 'Or continue with'}
              </span>
            </div>
          </div>

          {/* Google Sign-in Button */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading || isOtpLoading}
            className="w-full h-11"
          >
            {isGoogleLoading ? (
              <>
                <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                {t('auth.loggingIn') || 'Signing in...'}
              </>
            ) : (
              <>
                <svg className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-3'}`} viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                {t('auth.continueWithGoogle') || 'Continue with Google'}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Step 2: OTP Verification */}
      {currentStep === 'otp' && (
        <div className="space-y-4">
          {!embedded && (
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <MessageSquare className="h-5 w-5" />
                <h3 className="text-lg font-semibold">{t('otp.title') || 'Phone Verification'}</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                {otpMethod === 'sms'
                ? (t('otp.smsSubtitle') || "We've sent a verification code via SMS")
                : (t('otp.subtitle') || "We've sent a verification code to your WhatsApp")
              }
              </p>
            </div>
          )}
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              {otpMethod === 'sms'
                ? (t('otp.info.checkSms') || 'Please check your SMS messages')
                : (t('otp.info.checkWhatsapp') || 'Please check your WhatsApp messages')
              } ({verifiedPhone})
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('otp.otpLabel') || 'Verification Code'}
              </label>
              <OTPInput
                value={otpValue}
                onChange={setOtpValue}
                disabled={isOtpLoading}
                dir={dir}
                autoFocus={true}
                onComplete={handleOTPComplete}
                className="justify-center"
              />
            </div>

            {renderTimeRemaining()}

            <Button 
              ref={submitButtonRef}
              onClick={handleVerifyOTP}
              disabled={isOtpLoading || otpValue.length !== 4}
              className="w-full h-11"
            >
              {isOtpLoading ? (
                <>
                  <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  {t('otp.verifyOtp') || 'Verifying...'}
                </>
              ) : (
                t('otp.verifyOtp') || 'Verify Code'
              )}
            </Button>

            <div className="flex gap-2">
              <Button 
                type="button"
                variant="outline"
                onClick={handleBackToPhone}
                disabled={isOtpLoading}
                className="flex-1"
              >
                <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2 rotate-180' : 'mr-2'}`} />
                {t('otp.backToPhone') || 'Change Phone'}
              </Button>

              <Button 
                type="button"
                variant="outline"
                onClick={handleResendOTP}
                disabled={isResendLoading || resendCooldown > 0}
                className="flex-1"
              >
                {isResendLoading ? (
                  <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                ) : null}
                {resendCooldown > 0 
                  ? `${t('otp.resendOtp') || 'Resend'} (${resendCooldown}s)`
                  : t('otp.resendOtp') || 'Resend Code'
                }
              </Button>
            </div>
          </div>

          {resendCooldown === 0 && (
            <Alert>
              <AlertDescription>
                {t('otp.info.resendAvailable') || "Didn't receive the code? You can resend after 1 minute"}
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

       {/* Step 3: Group Selection for Existing Users */}
       {currentStep === 'groups' && (
         <div className="space-y-4">
           {!embedded && (
             <div className="text-center mb-4">
               <div className="flex items-center justify-center gap-2 mb-2">
                 <Users className="h-5 w-5" />
                 <h3 className="text-lg font-semibold">{t('publicRegistration.selectGroupTitle') || 'Select Your Section and Group'}</h3>
               </div>
               <p className="text-sm text-muted-foreground">
                 {t('publicRegistration.selectGroupSubtitle') || 'Please select the section and group you want to join.'}
               </p>
             </div>
           )}

           <Form {...groupForm}>
             <form onSubmit={groupForm.handleSubmit(handleGroupSelection)} className="space-y-4">
               <FormField
                 control={groupForm.control}
                 name="selectedSectionId"
                 render={({ field }) => (
                   <FormItem>
                     <FormLabel className="flex items-center gap-2">
                       <Users className="h-4 w-4" />
                       {t('publicRegistration.selectSection') || 'Select Section'}
                     </FormLabel>
                     <Select 
                       onValueChange={(value) => {
                         field.onChange(value);
                         groupForm.setValue('selectedGroupId', '');
                       }} 
                       defaultValue={field.value} 
                       disabled={isLoading}
                     >
                       <FormControl>
                         <SelectTrigger className="h-10 text-sm">
                           <SelectValue placeholder={t('publicRegistration.selectSectionPlaceholder') || 'Choose a section...'} />
                         </SelectTrigger>
                       </FormControl>
                       <SelectContent>
                         {sections.map((section) => (
                           <SelectItem key={section.id} value={section.id}>
                             <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
                               <span className="text-xs px-1.5 py-0.5 bg-slate-200 text-slate-700 rounded">
                                 {t('publicRegistration.section') || 'Section'}
                               </span>
                               <span>{section.name}</span>
                             </div>
                           </SelectItem>
                         ))}
                       </SelectContent>
                     </Select>
                     <FormMessage />
                   </FormItem>
                 )}
               />

               {selectedGroupSectionId && (
                 <FormField
                   control={groupForm.control}
                   name="selectedGroupId"
                   render={({ field }) => (
                     <FormItem>
                       <FormLabel className="flex items-center gap-2">
                         <Users className="h-4 w-4" />
                         {t('publicRegistration.selectGroup') || 'Select Group'}
                       </FormLabel>
                       <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isLoading}>
                         <FormControl>
                           <SelectTrigger className="h-10 text-sm">
                             <SelectValue placeholder={t('publicRegistration.selectGroupPlaceholder') || 'Choose a group...'} />
                           </SelectTrigger>
                         </FormControl>
                         <SelectContent>
                           {availableGroupsForExisting.length > 0 ? (
                             availableGroupsForExisting.map((group) => (
                               <SelectItem key={group.id} value={group.id}>
                                 <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
                                   <span className="text-xs px-1.5 py-0.5 bg-secondary text-secondary-foreground rounded">
                                     {t('publicRegistration.group') || 'Group'}
                                   </span>
                                   <span>{group.name}</span>
                                 </div>
                               </SelectItem>
                             ))
                           ) : (
                             <SelectItem value="no-groups" disabled>
                               {t('publicRegistration.noGroupsInSection') || 'No groups available in this section'}
                             </SelectItem>
                           )}
                         </SelectContent>
                       </Select>
                       <FormMessage />
                     </FormItem>
                   )}
                 />
               )}

               <div className="flex gap-2 pt-2">
                 <Button 
                   type="button"
                   variant="outline"
                   onClick={handleBackToPhone}
                   disabled={isLoading}
                   className="flex-1"
                 >
                   <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2 rotate-180' : 'mr-2'}`} />
                   {t('otp.backToPhone') || 'Back'}
                 </Button>

                 <Button 
                   type="submit" 
                   disabled={isLoading || !selectedGroupSectionId || !groupForm.watch('selectedGroupId')} 
                   className="flex-1 h-11"
                 >
                   {isLoading ? (
                     <>
                       <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                       {t('publicRegistration.joining') || 'Joining...'}
                     </>
                   ) : (
                     t('publicRegistration.joinOrganization') || 'Join Organization'
                   )}
                 </Button>
               </div>
             </form>
           </Form>
         </div>
       )}

       {/* Step 4: Registration Details */}
      {currentStep === 'details' && (
        <div className="space-y-4">
          {!embedded && (
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold">{title}</h3>
              </div>
            </div>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleFinalSubmit)} className="space-y-4">
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    {t('otp.success.phoneVerified') || 'Phone number verified successfully'}: {verifiedPhone}
                  </AlertDescription>
                </Alert>

                {/* Arabic Names First */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstNameAr"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('dashboard.manager.dashboard.customers.addDialog.firstNameAr') || 'First Name (Arabic)'}
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('dashboard.manager.dashboard.customers.addDialog.placeholders.firstNameAr') || 'أدخل الاسم الأول'} 
                            disabled={isLoading} 
                            className="h-10 text-sm" 
                            dir="rtl" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="lastNameAr"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('dashboard.manager.dashboard.customers.addDialog.lastNameAr') || 'Last Name (Arabic)'}
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('dashboard.manager.dashboard.customers.addDialog.placeholders.lastNameAr') || 'أدخل اسم العائلة'} 
                            disabled={isLoading} 
                            className="h-10 text-sm" 
                            dir="rtl" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* English Names */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstNameEn"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('dashboard.manager.dashboard.customers.addDialog.firstNameEn') || 'First Name (English)'}
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('dashboard.manager.dashboard.customers.addDialog.placeholders.firstNameEn') || 'Enter first name'} 
                            disabled={isLoading} 
                            className="h-10 text-sm" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="lastNameEn"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('dashboard.manager.dashboard.customers.addDialog.lastNameEn') || 'Last Name (English)'}
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('dashboard.manager.dashboard.customers.addDialog.placeholders.lastNameEn') || 'Enter last name'} 
                            disabled={isLoading} 
                            className="h-10 text-sm" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Section and Group Selection */}
                {showGroupSelection && hasJoinableGroups && sections.length > 0 && (
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="selectedSectionId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            {t('publicRegistration.selectSection') || 'Select Section'}
                          </FormLabel>
                          <Select 
                            onValueChange={(value) => {
                              field.onChange(value);
                              form.setValue('selectedGroupId', '');
                            }} 
                            defaultValue={field.value} 
                            disabled={isLoading}
                          >
                            <FormControl>
                              <SelectTrigger className="h-10 text-sm">
                                <SelectValue placeholder={t('publicRegistration.selectSectionPlaceholder') || 'Choose a section...'} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {sections.map((section) => (
                                <SelectItem key={section.id} value={section.id}>
                                  <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
                                    <span className="text-xs px-1.5 py-0.5 bg-slate-200 text-slate-700 rounded">
                                      {t('publicRegistration.section') || 'Section'}
                                    </span>
                                    <span>{section.name}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {selectedSectionId && (
                      <FormField
                        control={form.control}
                        name="selectedGroupId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex items-center gap-2">
                              <Users className="h-4 w-4" />
                              {t('publicRegistration.selectGroup') || 'Select Group'}
                            </FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isLoading}>
                              <FormControl>
                                <SelectTrigger className="h-10 text-sm">
                                  <SelectValue placeholder={t('publicRegistration.selectGroupPlaceholder') || 'Choose a group...'} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availableGroups.length > 0 ? (
                                  availableGroups.map((group) => (
                                    <SelectItem key={group.id} value={group.id}>
                                      <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
                                        <span className="text-xs px-1.5 py-0.5 bg-secondary text-secondary-foreground rounded">
                                          {t('publicRegistration.group') || 'Group'}
                                        </span>
                                        <span>{group.name}</span>
                                      </div>
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="no-groups" disabled>
                                    {t('publicRegistration.noGroupsInSection') || 'No groups available in this section'}
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={handleBackToPhone}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2 rotate-180' : 'mr-2'}`} />
                    {t('otp.backToPhone') || 'Change Phone'}
                  </Button>

                  <Button 
                    type="submit" 
                    disabled={isLoading} 
                    className="flex-1 h-11"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                        {t('publicRegistration.creating') || 'Creating...'}
                      </>
                    ) : (
                      t('publicRegistration.createAccount') || 'Create Account'
                    )}
                  </Button>
              </div>
            </form>
          </Form>
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div dir={dir}>{content}</div>;
  }

  return (
    <div dir={dir}>
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            {currentStep === 'phone' && <Phone className="h-5 w-5" />}
            {currentStep === 'otp' && <MessageSquare className="h-5 w-5" />}
           {currentStep === 'groups' && <Users className="h-5 w-5" />}
            {currentStep === 'details' && <CheckCircle className="h-5 w-5 text-green-600" />}
            {getStepTitle()}
          </CardTitle>
          {currentStep === 'otp' && (
            <p className="text-sm text-muted-foreground">
              {otpMethod === 'sms'
                ? (t('otp.smsSubtitle') || "We've sent a verification code via SMS")
                : (t('otp.subtitle') || "We've sent a verification code to your WhatsApp")
              }
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {content}
        </CardContent>
      </Card>
    </div>
  );
}
